import { useState } from 'react';
import { jobRoleGateway } from '../index';
import { isForbidden, jobRoleErrorMessage } from '../jobRoleErrors';
import { ConfirmDialog } from './ConfirmDialog';

export interface ArchiveRoleDialogProps {
  role: { id: string; title: string; employeesAssigned?: number | undefined; concurrencyVersion?: number | undefined };
  onClose: () => void;
  onArchived: () => void;
  onForbidden?: () => void;
}

/** Archive is the only way to retire a role: nothing is ever deleted. The dialog says exactly what happens before it happens. */
export function ArchiveRoleDialog({ role, onClose, onArchived, onForbidden }: ArchiveRoleDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await jobRoleGateway.archiveRole(role.id, role.concurrencyVersion);
      onArchived();
    } catch (err) {
      if (isForbidden(err)) onForbidden?.();
      setError(jobRoleErrorMessage(err, 'Could not archive this role.'));
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog title={`Archive ${role.title}?`} confirmLabel="Archive role" busy={busy} error={error} onCancel={onClose} onConfirm={() => void confirm()}>
      <p>Archiving does not delete this role or its history.</p>
      <ul className="jr-plain-list">
        <li>Employees who already have this role keep it, and it shows as archived on their profile.</li>
        <li>The role no longer appears when you add an employee or assign a role.</li>
        <li>You can restore it later from the Roles page.</li>
      </ul>
      {role.employeesAssigned !== undefined && role.employeesAssigned > 0 && (
        <p className="muted">
          {role.employeesAssigned === 1 ? '1 employee currently has' : `${role.employeesAssigned} employees currently have`} this role.
        </p>
      )}
    </ConfirmDialog>
  );
}
